import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import {
  Anchor, Compass, Sparkles, RefreshCw, ArrowDown, ArrowUp, Lock,
  Target, AlertTriangle, ChevronRight, Check, X, Database,
} from "lucide-react";

// Phase 36.1 — Hedging Co-Pilot panel
// Drops onto /app/commodities under the chart for the currently-selected commodity.

const fmtUsd = (n) => {
  if (n === null || n === undefined) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtNum = (n, dp = 0) => {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: dp });
};

const BAND_COLORS = {
  CONSERVATIVE: "border-[#FF3B30] text-[#FF3B30] bg-[#FF3B30]/5",
  BALANCED: "border-[#FFB000] text-[#FFB000] bg-[#FFB000]/5",
  OPPORTUNISTIC: "border-[#00E676] text-[#00E676] bg-[#00E676]/5",
};

export default function HedgingCopilotPanel({ commodityId, commodityMeta }) {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [picked, setPicked] = useState(null); // selected source object
  const [qtyCol, setQtyCol] = useState("");
  const [deliveryCol, setDeliveryCol] = useState("");
  const [priceCol, setPriceCol] = useState("");
  const [side, setSide] = useState("long");
  const [horizon, setHorizon] = useState(12);
  const [confidence, setConfidence] = useState(0.95);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Load latest persisted recommendation + source list when commodity changes
  useEffect(() => {
    if (!commodityId) return;
    setResult(null);
    Promise.all([
      api.get("/commodities/hedging-copilot/sources").catch(() => ({ data: [] })),
      api.get(`/commodities/${commodityId}/hedging-copilot/latest`).catch(() => ({ data: null })),
    ]).then(([s, latest]) => {
      setSources(s.data || []);
      if (latest.data) setResult(latest.data);
    });
  }, [commodityId]);

  // Heuristic auto-pick when a source is selected: try to guess Qty / Delivery / Price columns
  const pickSource = (sid) => {
    const s = sources.find(x => x.id === sid);
    setPicked(s || null);
    setResult(null); // a new source means the prior persisted result is no longer relevant
    if (!s) return;
    const cols = (s.columns || []).map(c => ({ raw: c, lower: c.toLowerCase() }));
    // score-ranked match — first matching keyword wins, and we exclude
    // identifier-like columns from the delivery candidate set so a CSV with
    // both `shipment_id` and `delivery_date` doesn't pick the id column.
    const findRanked = (keywordLists, opts = {}) => {
      const exclude = opts.excludeIdLike;
      for (const kws of keywordLists) {
        const hit = cols.find(c => {
          if (exclude && (c.lower.endsWith("_id") || c.lower.startsWith("id_") || c.lower === "id")) return false;
          return kws.some(k => c.lower.includes(k));
        });
        if (hit) return hit.raw;
      }
      return "";
    };
    setQtyCol(findRanked([
      ["qty", "quant"],
      ["tonn", "weight", "volume", "barrel", "ounce"],
    ], { excludeIdLike: true }));
    setDeliveryCol(findRanked([
      ["deliver", "delivery"],
      ["due_date", "due"],
      ["etd", "eta"],
      ["ship_date", "shipdate", "shipping_date"],
      ["date"],
    ], { excludeIdLike: true }));
    setPriceCol(findRanked([
      ["unit_price", "price"],
      ["rate", "fob", "cif"],
      ["amount", "value"],
    ], { excludeIdLike: true }));
  };

  const runCopilot = async () => {
    if (!picked) { toast.error("Pick a data source first."); return; }
    if (!qtyCol || !deliveryCol) {
      toast.error("Map both quantity and delivery-date columns."); return;
    }
    setRunning(true);
    try {
      const r = await api.post(`/commodities/${commodityId}/hedging-copilot`, {
        source_id: picked.id,
        qty_col: qtyCol,
        delivery_col: deliveryCol,
        price_col: priceCol || null,
        side,
        horizon_months: Number(horizon),
        confidence: Number(confidence),
      });
      setResult(r.data);
      toast.success("Hedging programme generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hedge co-pilot failed");
    } finally {
      setRunning(false);
    }
  };

  const acceptHedge = async () => {
    if (!result?.id) return;
    try {
      await api.post(`/commodities/${commodityId}/hedging-copilot/${result.id}/accept`);
      setResult({ ...result, status: "accepted" });
      toast.success("Programme accepted — added to your hedge ledger.", {
        duration: 8000,
        action: {
          label: "VIEW IN LEDGER →",
          onClick: () => navigate("/app/hedge-ledger"),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to accept");
    }
  };

  const dismissHedge = async () => {
    if (!result?.id) return;
    try {
      await api.post(`/commodities/${commodityId}/hedging-copilot/${result.id}/dismiss`);
      setResult({ ...result, status: "dismissed" });
      toast.success("Programme dismissed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to dismiss");
    }
  };

  const monthLabels = useMemo(() => result?.exposure?.months || [], [result]);

  return (
    <Panel
      title="// HEDGING CO-PILOT · DARA-GROUNDED"
      meta={commodityMeta?.short || ""}
      testId="hedging-copilot-panel"
    >
      <div className="p-4 space-y-4">
        {/* Empty-sources guard — Adrian-style users get clear next-step */}
        {sources.length === 0 && (
          <div
            className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-4 flex items-start gap-3"
            data-testid="hedge-no-sources-banner"
          >
            <Database className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#FFB000] font-bold">
                No data source available
              </div>
              <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                The Hedge Co-Pilot reads your open shipment / sales book to project
                exposure. Upload a CSV with at minimum a <span className="text-white font-bold">delivery date</span> and{" "}
                <span className="text-white font-bold">quantity</span> column (price column optional but recommended).
              </p>
              <Link
                to="/app/sources"
                data-testid="hedge-upload-source-cta"
                className="inline-flex items-center gap-2 mt-3 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90"
              >
                <ChevronRight className="w-3.5 h-3.5" /> UPLOAD SHIPMENT / SALES CSV
              </Link>
            </div>
          </div>
        )}

        {/* Setup row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <label className="block lg:col-span-5">
            <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SHIPMENT / SALES BOOK</span>
            <select
              value={picked?.id || ""}
              onChange={(e) => pickSource(e.target.value)}
              data-testid="hedge-source-picker"
              className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
            >
              <option value="">— Select a data source —</option>
              {sources.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.origin || s.type}
                </option>
              ))}
            </select>
          </label>
          <label className="block lg:col-span-2">
            <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">SIDE</span>
            <select
              value={side}
              onChange={(e) => setSide(e.target.value)}
              data-testid="hedge-side"
              className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
            >
              <option value="long">LONG (producer)</option>
              <option value="short">SHORT (consumer)</option>
            </select>
          </label>
          <label className="block lg:col-span-2">
            <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">HORIZON · MONTHS</span>
            <input
              type="number"
              min="1"
              max="36"
              value={horizon}
              onChange={(e) => setHorizon(e.target.value)}
              data-testid="hedge-horizon"
              className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
            />
          </label>
          <label className="block lg:col-span-3">
            <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">VAR CONFIDENCE</span>
            <select
              value={confidence}
              onChange={(e) => setConfidence(e.target.value)}
              data-testid="hedge-confidence"
              className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
            >
              <option value={0.90}>90% (typical)</option>
              <option value={0.95}>95% (board default)</option>
              <option value={0.99}>99% (regulator-grade)</option>
            </select>
          </label>
        </div>

        {/* Column mapping row — only when a source is picked */}
        {picked && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { state: qtyCol, set: setQtyCol, label: "QTY COLUMN", testid: "hedge-qty-col", required: true },
              { state: deliveryCol, set: setDeliveryCol, label: "DELIVERY DATE COLUMN", testid: "hedge-delivery-col", required: true },
              { state: priceCol, set: setPriceCol, label: "PRICE COLUMN (OPTIONAL)", testid: "hedge-price-col", required: false },
            ].map(f => (
              <label key={f.label} className="block">
                <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                  {f.label}{f.required && <span className="text-[#FF3B30]"> *</span>}
                </span>
                <select
                  value={f.state}
                  onChange={(e) => f.set(e.target.value)}
                  data-testid={f.testid}
                  className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                >
                  <option value="">— pick a column —</option>
                  {(picked.columns || []).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        )}

        {/* Run button */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-mono text-[10px] text-zinc-600 max-w-xl">
            DARA reads the open book, projects unhedged exposure, computes parametric VaR
            and asks DARA for specific contracts to lock in margin.
          </div>
          <button
            onClick={runCopilot}
            disabled={running || !picked}
            data-testid="hedge-run-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
          >
            {running ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> RUNNING…</>
              : <><Compass className="w-3.5 h-3.5" /> RUN CO-PILOT</>}
          </button>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-4 pt-2 border-t border-zinc-900" data-testid="hedge-result">
            {/* Headline strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900">
              <Stat
                label="UNHEDGED EXPOSURE"
                value={`${fmtNum(result.exposure?.total_qty, 1)} ${result.contract_meta?.lot_unit || ""}`}
                accent="white"
              />
              <Stat
                label={`$ AT RISK · ${(result.var?.confidence * 100).toFixed(0)}%`}
                value={fmtUsd(result.var?.dollar_at_risk)}
                accent="red"
                sub={`${result.var?.move_pct}% move @ ${result.var?.adverse_price}`}
              />
              <Stat
                label="WORST · UNHEDGED"
                value={fmtUsd(result.pnl_band?.worst_case_unhedged_usd)}
                accent="red"
              />
              <Stat
                label="WORST · HEDGED"
                value={fmtUsd(result.pnl_band?.worst_case_hedged_usd)}
                accent="green"
                sub="DOWNSIDE CAPPED"
              />
            </div>

            {/* Recommendation banner */}
            <div className={`border-l-2 px-4 py-3 ${BAND_COLORS[result.recommended_band] || BAND_COLORS.BALANCED}`}>
              <div className="flex items-start gap-3">
                <Target className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest mb-1">
                    RECOMMENDED BAND · {result.recommended_band || "—"}
                  </div>
                  <p className="text-sm text-zinc-200 leading-relaxed">
                    {result.recommended_rationale}
                  </p>
                </div>
              </div>
            </div>

            {/* Accept / Dismiss action row */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                STATUS ·{" "}
                <span className={
                  result.status === "accepted" ? "text-[#00E676]"
                  : result.status === "dismissed" ? "text-zinc-500"
                  : result.status === "closed" ? "text-[#FFB000]"
                  : "text-[#FFB000]"
                }>
                  {(result.status || "draft").toUpperCase()}
                </span>
                {result.status === "accepted" && (
                  <span className="text-zinc-600 ml-2">· Tracked in Hedge Ledger</span>
                )}
              </div>
              {result.status !== "accepted" && result.status !== "closed" && (
                <div className="flex gap-2">
                  <button
                    onClick={dismissHedge}
                    data-testid="hedge-dismiss-btn"
                    className="border border-zinc-800 hover:border-zinc-700 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-2"
                  >
                    <X className="w-3.5 h-3.5" /> DISMISS
                  </button>
                  <button
                    onClick={acceptHedge}
                    data-testid="hedge-accept-btn"
                    className="bg-[#00E676] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:opacity-90 flex items-center gap-2"
                  >
                    <Check className="w-3.5 h-3.5" /> ACCEPT PROGRAMME
                  </button>
                </div>
              )}
            </div>

            {/* Programme legs */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">PROGRAMME · LEG-BY-LEG</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {(result.programme || []).map((leg, i) => (
                  <div key={i} className="border border-zinc-800 bg-zinc-950/40 p-3" data-testid={`hedge-leg-${i + 1}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">LEG {leg.leg || i + 1}</span>
                        <span
                          className={`px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest border ${
                            leg.side === "BUY"
                              ? "border-[#00E676]/40 text-[#00E676] bg-[#00E676]/5"
                              : "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/5"
                          }`}
                        >
                          {leg.side === "BUY" ? <ArrowUp className="inline w-3 h-3 mr-0.5" /> : <ArrowDown className="inline w-3 h-3 mr-0.5" />}
                          {leg.side}
                        </span>
                      </div>
                      <span className="font-mono text-[10px] text-[#FFB000]">{leg.tenor}</span>
                    </div>
                    <div className="font-heading font-bold text-sm tracking-tight mb-1">{leg.instrument}</div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <Mini label="LOTS" value={fmtNum(leg.lots)} />
                      <Mini label="QTY" value={fmtNum(leg.qty, 1)} />
                      <Mini label="REF" value={fmtNum(leg.reference_price, 2)} />
                    </div>
                    <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{leg.purpose}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Caveats + Monitor */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-l-2 border-[#FF3B30] pl-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> CAVEATS
                </div>
                <ul className="space-y-1">
                  {(result.caveats || []).map((c, i) => (
                    <li key={i} className="font-mono text-[11px] text-zinc-400 flex gap-2">
                      <span className="text-zinc-700">·</span><span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-l-2 border-[#FFB000] pl-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2 flex items-center gap-1.5">
                  <Anchor className="w-3 h-3" /> MONITOR
                </div>
                <ul className="space-y-1">
                  {(result.monitor || []).map((c, i) => (
                    <li key={i} className="font-mono text-[11px] text-zinc-400 flex gap-2">
                      <ChevronRight className="w-3 h-3 text-[#FFB000] flex-shrink-0 mt-0.5" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Exposure schedule */}
            {monthLabels.length > 0 && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> EXPOSURE SCHEDULE
                </div>
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left font-mono text-[9px] uppercase tracking-widest text-zinc-500 py-2 pr-3">MONTH</th>
                        <th className="text-right font-mono text-[9px] uppercase tracking-widest text-zinc-500 py-2 pr-3">QTY</th>
                        <th className="text-right font-mono text-[9px] uppercase tracking-widest text-zinc-500 py-2 pr-3">REALISED AVG</th>
                        <th className="text-right font-mono text-[9px] uppercase tracking-widest text-zinc-500 py-2">ROWS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthLabels.map((m, i) => (
                        <tr key={i} className="border-b border-zinc-900">
                          <td className="font-mono py-1.5 pr-3 text-zinc-200">{m.month}</td>
                          <td className="font-mono py-1.5 pr-3 text-right text-white">{fmtNum(m.qty, 1)}</td>
                          <td className="font-mono py-1.5 pr-3 text-right text-zinc-400">{m.weighted_avg_price ? fmtNum(m.weighted_avg_price, 2) : "—"}</td>
                          <td className="font-mono py-1.5 text-right text-zinc-600">{m.row_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="font-mono text-[9px] text-zinc-600 pt-2 border-t border-zinc-900 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#FFB000]" />
              Generated {new Date(result.created_at).toLocaleString()} · DARA Engine · DARA Co-Pilot
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

function Stat({ label, value, accent, sub }) {
  const cls = accent === "red" ? "text-[#FF3B30]"
    : accent === "green" ? "text-[#00E676]"
    : accent === "amber" ? "text-[#FFB000]" : "text-white";
  return (
    <div className="bg-black p-3">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono text-lg font-medium mt-0.5 ${cls}`}>{value}</div>
      {sub && <div className="font-mono text-[9px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono text-sm text-white">{value}</div>
    </div>
  );
}
